// Imagesgallery.js - Same UI as Brands/Variations

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaImage, FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble, FaEllipsisV } from 'react-icons/fa';
import { IoImagesSharp } from "react-icons/io5";
import axios from 'axios';
import Swal from 'sweetalert2';
import './Imagesgallery.css';

// Last loaded galleries of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const IMAGES_CACHE_PREFIX = 'imagesgallery_cache_';

const readImagesCache = (id) => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(`${IMAGES_CACHE_PREFIX}${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeImagesCache = (id, data) => {
  if (!id) return;
  try {
    localStorage.setItem(`${IMAGES_CACHE_PREFIX}${id}`, JSON.stringify(data));
  } catch (e) {}
};

const getUrlShopId = () => {
  const match = window.location.pathname.match(/\/shop\/(\d+)/);
  return match ? parseInt(match[1]) : null;
};

const Imagesgallery = () => {
  const initialShopId = getUrlShopId();

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [image1, setImage1] = useState(null);
  const [image2, setImage2] = useState(null);
  const [image3, setImage3] = useState(null);
  const [image4, setImage4] = useState(null);
  const [preview1, setPreview1] = useState('');
  const [preview2, setPreview2] = useState('');
  const [preview3, setPreview3] = useState('');
  const [preview4, setPreview4] = useState('');
  const [fileName1, setFileName1] = useState('');
  const [fileName2, setFileName2] = useState('');
  const [fileName3, setFileName3] = useState('');
  const [fileName4, setFileName4] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [images, setImages] = useState(() => readImagesCache(initialShopId) || []);
  // True only while there is nothing to show yet (so "No images found" never flashes)
  const [isLoading, setIsLoading] = useState(() => !readImagesCache(initialShopId));
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState(initialShopId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentShopName, setCurrentShopName] = useState('');

  const [currentImage1, setCurrentImage1] = useState('');
  const [currentImage2, setCurrentImage2] = useState('');
  const [currentImage3, setCurrentImage3] = useState('');
  const [currentImage4, setCurrentImage4] = useState('');

  const BASE_URL = 'https://super-backend.hira-tech.co.uk/api/product';
  const imagesRequestRef = useRef(0);
  const shopsLoadedRef = useRef(false);
  const previousShopIdRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  const showSuccessAlert = (message) => {
    Swal.fire({ icon: 'success', title: 'Success!', text: message, timer: 2000, showConfirmButton: true, confirmButtonColor: '#3085d6' });
  };

  const showErrorAlert = (message) => {
    Swal.fire({ icon: 'error', title: 'Error!', text: message, confirmButtonColor: '#d33' });
  };

  const showWarningAlert = (message) => {
    Swal.fire({ icon: 'warning', title: 'Warning!', text: message, confirmButtonColor: '#f0ad4e' });
  };

  const showDeleteConfirmation = (title, isMultiple = false, count = 1) => {
    const message = isMultiple
      ? `Are you sure you want to delete ${count} selected image(s)?`
      : `Are you sure you want to delete "${title}"?`;
    return Swal.fire({
      title: 'Are you sure?', text: message, icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6', confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  useEffect(() => {
    const fetchShops = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/shops/`, { timeout: 10000 });
        const shopsData = Array.isArray(response.data) ? response.data : [];
        setShops(shopsData);
        shopsLoadedRef.current = true;
        // No shop in the URL and no shops at all: nothing will ever load
        if (!getUrlShopId() && shopsData.length === 0) setIsLoading(false);
      } catch (err) {
        console.error('Error fetching shops:', err.message);
        setShops([]);
        shopsLoadedRef.current = true;
        if (!getUrlShopId()) setIsLoading(false);
      }
    };
    if (!shopsLoadedRef.current) fetchShops();
  }, []);

  // Keep the shop name correct once the shops list is available
  useEffect(() => {
    if (selectedShopId) {
      const shop = shops.find(s => s.shop_id === selectedShopId);
      setCurrentShopName(shop ? shop.shop_name : `Shop ${selectedShopId}`);
    }
  }, [shops, selectedShopId]);

  const getShopIdFromUrl = useCallback(() => {
    const pathname = window.location.pathname;
    const match = pathname.match(/\/shop\/(\d+)/);

    if (match) {
      const shopId = parseInt(match[1]);
      if (shopId !== previousShopIdRef.current || isInitialLoadRef.current) {
        previousShopIdRef.current = shopId;
        isInitialLoadRef.current = false;
        setSelectedShopId(shopId);

        const shop = shops.find(s => s.shop_id === shopId);
        if (shop) setCurrentShopName(shop.shop_name);
        else setCurrentShopName(`Shop ${shopId}`);

        setSelectedImages([]);
        setShowActionDropdown(false);
      }
    } else if (shops.length > 0 && !selectedShopId) {
      const firstShop = shops[0];
      window.history.pushState({}, '', `/shop/${firstShop.shop_id}/Imagesgallery`);
      previousShopIdRef.current = firstShop.shop_id;
      setSelectedShopId(firstShop.shop_id);
      setCurrentShopName(firstShop.shop_name);
    }
  }, [shops, selectedShopId]);

  useEffect(() => {
    getShopIdFromUrl();
    window.addEventListener('popstate', getShopIdFromUrl);
    const handleShopChange = () => getShopIdFromUrl();
    window.addEventListener('shopChanged', handleShopChange);
    const urlCheckInterval = setInterval(() => {
      const pathname = window.location.pathname;
      const match = pathname.match(/\/shop\/(\d+)/);
      if (match) {
        const currentShopId = parseInt(match[1]);
        if (currentShopId !== previousShopIdRef.current) getShopIdFromUrl();
      }
    }, 500);
    return () => {
      window.removeEventListener('popstate', getShopIdFromUrl);
      window.removeEventListener('shopChanged', handleShopChange);
      clearInterval(urlCheckInterval);
    };
  }, [getShopIdFromUrl]);

  const loadImagesFromBackend = useCallback(async () => {
    if (!selectedShopId) return;

    // Only the latest request is allowed to update the screen
    const requestId = ++imagesRequestRef.current;

    setError(null);
    try {
      let url = `${BASE_URL}/image-galleries/?shop_id=${selectedShopId}`;
      const response = await axios.get(url, { timeout: 10000 });
      if (requestId !== imagesRequestRef.current) return;

      let galleries = [];
      if (response.data && response.data.data) galleries = response.data.data;
      else if (Array.isArray(response.data)) galleries = response.data;

      if (galleries.length > 0) {
        galleries.sort((a, b) => a.id - b.id);
        const numberedImages = galleries.map((img, index) => ({
          ...img,
          title: img.title || `Image Gallery ${index + 1}`
        }));
        setImages(numberedImages);
        writeImagesCache(selectedShopId, numberedImages);
      } else {
        setImages([]);
        writeImagesCache(selectedShopId, []);
      }
      setError(null);
    } catch (err) {
      if (requestId !== imagesRequestRef.current) return;
      console.error('Error loading images:', err);
      let errorMsg = 'Failed to load images. ';
      if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        errorMsg += 'Cannot connect to backend server.';
      }
      setError(errorMsg);
      setImages([]);
    } finally {
      if (requestId === imagesRequestRef.current) setIsLoading(false);
    }
  }, [selectedShopId]);

  useEffect(() => {
    if (!selectedShopId) return;

    // Show the saved list of this shop immediately (if there is one)
    const cached = readImagesCache(selectedShopId);
    if (cached) {
      setImages(cached);
      setIsLoading(false);
    } else {
      setImages([]);
      setIsLoading(true);
    }

    loadImagesFromBackend();
  }, [selectedShopId, loadImagesFromBackend]);

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const filteredImages = images.filter(image =>
    image.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectedImages.length === filteredImages.length && filteredImages.length > 0) {
      setSelectedImages([]);
    } else {
      setSelectedImages(filteredImages.map(image => image.id));
    }
    setShowActionDropdown(false);
  };

  const handleSelectImage = (imageId) => {
    if (selectedImages.includes(imageId)) setSelectedImages(selectedImages.filter(id => id !== imageId));
    else setSelectedImages([...selectedImages, imageId]);
  };

  const handleDeleteSelected = async () => {
    if (selectedImages.length === 0) { showWarningAlert('Please select images to delete'); return; }
    const result = await showDeleteConfirmation('', true, selectedImages.length);
    if (result.isConfirmed) {
      setLoading(true);
      try {
        let deletedCount = 0;
        for (const imageId of selectedImages) {
          await axios.delete(`${BASE_URL}/image-galleries/${imageId}/`);
          deletedCount++;
        }
        await loadImagesFromBackend();
        setSelectedImages([]);
        setShowActionDropdown(false);
        showSuccessAlert(`${deletedCount} image(s) deleted successfully!`);
      } catch (err) {
        showErrorAlert('Error deleting images. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClearAll = () => { setSelectedImages([]); setShowActionDropdown(false); };

  const handleOpenModalForNew = () => {
    setEditingId(null);
    setPreview1(''); setPreview2(''); setPreview3(''); setPreview4('');
    setImage1(null); setImage2(null); setImage3(null); setImage4(null);
    setFileName1(''); setFileName2(''); setFileName3(''); setFileName4('');
    setCurrentImage1(''); setCurrentImage2(''); setCurrentImage3(''); setCurrentImage4('');
    setIsModalOpen(true);
  };

  const handleOpenModalForEdit = (image) => {
    setEditingId(image.id);
    setPreview1(image.image1_url || '');
    setPreview2(image.image2_url || '');
    setPreview3(image.image3_url || '');
    setPreview4(image.image4_url || '');
    setImage1(null); setImage2(null); setImage3(null); setImage4(null);
    setFileName1(''); setFileName2(''); setFileName3(''); setFileName4('');
    setCurrentImage1(image.image1 ? image.image1.split('/').pop() : '');
    setCurrentImage2(image.image2 ? image.image2.split('/').pop() : '');
    setCurrentImage3(image.image3 ? image.image3.split('/').pop() : '');
    setCurrentImage4(image.image4 ? image.image4.split('/').pop() : '');
    setIsModalOpen(true);
  };

  const handleImageChange = (e, imageNumber) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        switch(imageNumber) {
          case 1: setPreview1(reader.result); setImage1(file); setFileName1(file.name); setCurrentImage1(''); break;
          case 2: setPreview2(reader.result); setImage2(file); setFileName2(file.name); setCurrentImage2(''); break;
          case 3: setPreview3(reader.result); setImage3(file); setFileName3(file.name); setCurrentImage3(''); break;
          case 4: setPreview4(reader.result); setImage4(file); setFileName4(file.name); setCurrentImage4(''); break;
          default: break;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearCurrentImage = (imageNumber) => {
    switch(imageNumber) {
      case 1: setPreview1(''); setCurrentImage1(''); setFileName1(''); setImage1(null); break;
      case 2: setPreview2(''); setCurrentImage2(''); setFileName2(''); setImage2(null); break;
      case 3: setPreview3(''); setCurrentImage3(''); setFileName3(''); setImage3(null); break;
      case 4: setPreview4(''); setCurrentImage4(''); setFileName4(''); setImage4(null); break;
      default: break;
    }
  };

  const handleSave = async () => {
    if (!selectedShopId) { showWarningAlert('No shop selected.'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      if (!editingId) {
        formData.append('shop_id', selectedShopId);
        const nextNumber = images.length + 1;
        formData.append('title', `Image Gallery ${nextNumber}`);
        if (image1) formData.append('image1', image1);
        if (image2) formData.append('image2', image2);
        if (image3) formData.append('image3', image3);
        if (image4) formData.append('image4', image4);
        await axios.post(`${BASE_URL}/image-galleries/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        showSuccessAlert('Image added successfully!');
      } else {
        if (image1) formData.append('image1', image1);
        if (image2) formData.append('image2', image2);
        if (image3) formData.append('image3', image3);
        if (image4) formData.append('image4', image4);
        await axios.put(`${BASE_URL}/image-galleries/${editingId}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        showSuccessAlert('Image updated successfully!');
      }
      await loadImagesFromBackend();
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      console.error('Error saving image:', err);
      showErrorAlert(`Error saving image: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPreview1(''); setPreview2(''); setPreview3(''); setPreview4('');
    setImage1(null); setImage2(null); setImage3(null); setImage4(null);
    setFileName1(''); setFileName2(''); setFileName3(''); setFileName4('');
    setCurrentImage1(''); setCurrentImage2(''); setCurrentImage3(''); setCurrentImage4('');
    setEditingId(null);
  };

  const handleDelete = async () => {
    const result = await showDeleteConfirmation('this image', false);
    if (result.isConfirmed && editingId !== null) {
      setLoading(true);
      try {
        await axios.delete(`${BASE_URL}/image-galleries/${editingId}/`);
        await loadImagesFromBackend();
        setSelectedImages(selectedImages.filter(id => id !== editingId));
        showSuccessAlert('Image deleted successfully!');
      } catch (err) {
        showErrorAlert('Error deleting image. Please try again.');
      } finally {
        setLoading(false);
      }
      setIsModalOpen(false);
      resetForm();
    }
  };

  const handleDeleteFromView = async (id, title) => {
    const result = await showDeleteConfirmation(title, false);
    if (result.isConfirmed) {
      setLoading(true);
      try {
        await axios.delete(`${BASE_URL}/image-galleries/${id}/`);
        await loadImagesFromBackend();
        setSelectedImages(selectedImages.filter(selectedId => selectedId !== id));
        showSuccessAlert('Image deleted successfully!');
      } catch (err) {
        showErrorAlert('Error deleting image. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => { setIsModalOpen(false); resetForm(); };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showActionDropdown]);

  const getFileExtension = (filename) => {
    if (!filename) return '';
    const parts = filename.split('.');
    return parts[parts.length - 1].toUpperCase();
  };

  const isAllSelected = filteredImages.length > 0 && selectedImages.length === filteredImages.length;

  return (
    <div className="gallery-main-container">

      {/* Heading + Search + Buttons */}
      <div className="gallery-action-section">
        <div className="gallery-heading">
          <h2>
            <IoImagesSharp className="gallery-heading-icon" />
            Image Gallery
          </h2>
        </div>

        <div className="gallery-action-right">
          <div className="gallery-search-wrapper">
            <FaSearch className="gallery-search-icon" />
            <input
              type="text"
              placeholder="Search images by title..."
              value={searchTerm}
              onChange={handleSearch}
              className="gallery-search-input"
            />
          </div>

          <div className="gallery-action-buttons">
            <button
              className="gallery-select-all-btn"
              onClick={handleSelectAll}
            >
              <FaCheckDouble />
              <span>{isAllSelected ? 'Deselect All' : 'Select All'}</span>
            </button>
            <button
              className="gallery-add-btn"
              onClick={handleOpenModalForNew}
            >
              <FaPlus />
              <span>ADD NEW IMAGE</span>
            </button>
          </div>
        </div>
      </div>

      {selectedImages.length > 0 && (
        <div className="selected-summary">
          <div className="summary-content">
            <span className="summary-icon">✓</span>
            <span className="summary-text">
              {selectedImages.length} image(s) selected
            </span>
            <div className="action-dropdown-container">
              <button
                className="action-dropdown-btn"
                onClick={() => setShowActionDropdown(!showActionDropdown)}
              >
                <FaEllipsisV />
                <span>Actions</span>
              </button>
              {showActionDropdown && (
                <div className="action-dropdown-menu">
                  <button
                    className="dropdown-item delete-item"
                    onClick={handleDeleteSelected}
                  >
                    <FaTrash />
                    <span>Delete Selected ({selectedImages.length})</span>
                  </button>
                </div>
              )}
            </div>
            <button className="clear-selection" onClick={handleClearAll}>
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* TABLE with grey border */}
      <div className="gallery-table-container">
        <div className="gallery-table-wrapper">
          <table className="gallery-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>IMAGE GALLERY</th>
                <th style={{ width: '100px' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {/* Nothing is shown until the first load is done, so "No images found" never flashes */}
              {isLoading ? (
                <tr>
                  <td colSpan="3" style={{ height: '200px' }}></td>
                </tr>
              ) : filteredImages.length > 0 ? (
                filteredImages.map((image) => {
                  const isSelected = selectedImages.includes(image.id);
                  return (
                    <tr key={image.id} className={isSelected ? 'gallery-selected-row' : ''}>
                      <td className="gallery-checkbox-cell">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectImage(image.id)}
                        />
                      </td>
                      <td className="gallery-title-cell">
                        <span className="gallery-title">{image.title}</span>
                      </td>
                      <td className="gallery-actions-cell">
                        <button
                          className="gallery-edit-btn"
                          onClick={() => handleOpenModalForEdit(image)}
                          title="Edit Images"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="gallery-delete-btn"
                          onClick={() => handleDeleteFromView(image.id, image.title)}
                          title="Delete Image"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="3" className="gallery-no-data-row">
                    <div className="gallery-no-data">
                      <FaImage className="gallery-no-data-icon" />
                      <p>No images found for {currentShopName || 'this shop'}.</p>
                      <button className="gallery-add-first-btn" onClick={handleOpenModalForNew}>
                        <FaPlus /> Add Your First Image
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="gallery-modal-overlay" onClick={handleCloseModal}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-modal-header">
              <h3>{editingId !== null ? 'Edit Images' : 'Add New Images'}</h3>
              <button className="gallery-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="gallery-modal-body">
              <div className="form-group">
                <label className="gallery-form-label">SHOP:</label>
                <div className="gallery-shop-info">
                  {currentShopName}
                </div>
              </div>

              <form className="image-form">
                {[1, 2, 3, 4].map(num => {
                  const currentImg = `currentImage${num}`;
                  const previewKey = `preview${num}`;
                  const fileNameKey = `fileName${num}`;

                  return (
                    <div className="form-group gallery-image-form-group" key={num}>
                      <label className="gallery-form-label">IMAGE {num}:</label>

                      {editingId && eval(currentImg) && !eval(fileNameKey) && (
                        <div className="current-image-info">
                          <span className="current-label">Currently:</span>
                          <span className="current-file-name">{eval(currentImg)}</span>
                          <button
                            type="button"
                            className="clear-current-btn"
                            onClick={() => handleClearCurrentImage(num)}
                          >
                            Clear
                          </button>
                        </div>
                      )}

                      <div className="image-upload-container">
                        <span className="change-label">
                          {editingId && eval(currentImg) ? 'Change:' : 'Upload:'}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageChange(e, num)}
                          className="image-input"
                          id={`image-upload-${num}`}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor={`image-upload-${num}`} className="image-upload-label">
                          Choose File
                        </label>
                        <span className="file-name-display">
                          {eval(fileNameKey) ? (
                            <>
                              <span className="file-name">{eval(fileNameKey)}</span>
                              <span className="file-type">({getFileExtension(eval(fileNameKey))})</span>
                            </>
                          ) : (eval(currentImg) || 'No file chosen')}
                        </span>
                        {eval(fileNameKey) && (
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => {
                              switch(num) {
                                case 1: setPreview1(''); setImage1(null); setFileName1(''); break;
                                case 2: setPreview2(''); setImage2(null); setFileName2(''); break;
                                case 3: setPreview3(''); setImage3(null); setFileName3(''); break;
                                case 4: setPreview4(''); setImage4(null); setFileName4(''); break;
                                default: break;
                              }
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>

                      {eval(previewKey) && (
                        <div className="image-preview-modal">
                          <img
                            src={eval(previewKey)}
                            alt={`Preview ${num}`}
                            className="preview-image-modal"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </form>
            </div>

            <div className="gallery-modal-footer">
              <button className="gallery-btn-cancel" onClick={handleCloseModal}>
                Cancel
              </button>
              {editingId !== null && (
                <button className="gallery-btn-delete" onClick={handleDelete}>
                  Delete
                </button>
              )}
              <button
                className="gallery-btn-save"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : (editingId !== null ? 'Update Images' : 'Save Images')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Imagesgallery;